import React from 'react'
import { Col, Row } from 'react-bootstrap'
import * as Request from '../../utilities/request'
import { AppContext } from '../../App'
import { Invoice } from '../../../../back-end/common/invoice'
import { Loading } from '../ui/Loading/Loading'
import { dollarFormat } from '../../utilities/format'
import { DateTime } from 'luxon'
import { Link } from 'react-router-dom'
import DashboardCard, { HeaderBr } from './dashboardCard'

interface RecentInvoicesProps {
	directorXpmID?: string
	showMonthCount?: boolean
	onlySelfRaised?: boolean
	replaceDueDateWithStatus?: boolean
	isDirectorViewing?: boolean
	viewingUserID?: string
}

const RecentInvoices = (props: RecentInvoicesProps) => {
	const { appState } = React.useContext(AppContext)

	const [invoices, setInvoices] = React.useState<Invoice[] | null>(null)

	React.useEffect(() => {
		const getData = async () => {
			const [invoiceRes] = await Promise.all([
				Request.get(`invoice?minify&where=InvoiceDate>${DateTime.utc().minus({ months: 2 }).toFormat('yyyy-MM-dd')}`),
			])

			setInvoices(invoiceRes.data.invoices)
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState])

	if (!invoices) {
		return (
			<DashboardCard background="cornflower">
				<div className="center-flex">
					<Loading show />
				</div>
			</DashboardCard>
		)
	}

	const directorFilter = (invoice: Invoice) => (props.directorXpmID ? invoice.client?.accountManagerStaffXpmID === props.directorXpmID : true)

	// Directors only see awaiting approval, supers/managers see draft, others see all
	const statusFilter = (invoice: Invoice) =>
		typeof props.isDirectorViewing === 'boolean' ? (props.isDirectorViewing ? invoice.status === 'SUBMITTED' : invoice.status === 'DRAFT') : true

	const selfRaisedFilter = (invoice: Invoice) =>
		invoice && props.onlySelfRaised && props.viewingUserID ? invoice.createUserID === props.viewingUserID : true

	const invoicesToRender = invoices.filter(directorFilter).filter(statusFilter).filter(selfRaisedFilter)

	const colWidths = {
		invNum: 2,
		client: 4,
		dueDate: 3,
		balance: 3,
	}

	return (
		<DashboardCard background="cornflower">
			{props.showMonthCount && (
				<Row className="pb-4">
					<Col className="d-flex flex-column">
						<span className="span-body-2 span-bold span-black">INVOICES DUE THIS MONTH</span>
						<span className="big-blue-number">
							{
								invoices
									.filter(directorFilter)
									.filter(
										(invoice) =>
											invoice.dueDate >= DateTime.now().startOf('month').toISO() &&
											invoice.dueDate <= DateTime.now().endOf('month').toISO()
									).length
							}
						</span>
					</Col>
				</Row>
			)}
			{props.isDirectorViewing && (
				<Row className="pb-4">
					<Col className="d-flex flex-column">
						<span className="span-body-2 span-bold span-black">INVOICES AWAITING YOUR ACTION</span>
						<span className="big-blue-number">{invoicesToRender.length}</span>
					</Col>
				</Row>
			)}
			{props.onlySelfRaised && (
				<Row className="pb-4">
					<Col className="d-flex flex-column">
						<span className="span-body-2 span-bold span-black">LAST 20 INVOICES RAISED</span>
					</Col>
				</Row>
			)}
			{invoicesToRender.length > 0 && (
				<>
					<Row className="pb-2">
						<Col sm={colWidths.invNum}>
							<span className="span-body-2 span-bold span-black">Inv No.</span>
						</Col>
						<Col sm={colWidths.client}>
							<span className="span-body-2 span-bold span-black">Client</span>
						</Col>
						<Col sm={colWidths.dueDate}>
							<span className="span-body-2 span-bold span-black">{props.replaceDueDateWithStatus ? 'Status' : 'Due Date'}</span>
						</Col>
						<Col sm={colWidths.balance}>
							<span className="span-body-2 span-bold span-black">Balance</span>
						</Col>
					</Row>
					<HeaderBr color="black" />
				</>
			)}
			{invoicesToRender
				.sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))
				.slice(0, 20)
				.map((invoice) => (
					<Row key={invoice.invoiceID} className="py-2">
						<Col sm={colWidths.invNum}>
							{/* TODO: Make this a link when we add link generation to invoices */}
							<span className="span-body-2 span-black">{invoice.number || '-'}</span>
						</Col>
						<Col sm={colWidths.client}>
							<span className="span-body-2 span-black">{invoice.client?.name}</span>
						</Col>
						<Col sm={colWidths.dueDate}>
							<span className="span-body-2 span-black">
								{props.replaceDueDateWithStatus ? invoice.status : DateTime.fromISO(invoice.dueDate).toFormat('d/M/yy')}
							</span>
						</Col>
						<Col sm={colWidths.balance}>
							<span className="span-body-2 span-black">{dollarFormat(invoice.amountDue)}</span>
						</Col>
					</Row>
				))}
			<Row>
				<Col />
				<Col sm="auto">
					<Link to={'/invoices'} className="span-body-1 span-bold">
						View All Invoices
					</Link>
				</Col>
			</Row>
		</DashboardCard>
	)
}

export default RecentInvoices
